import { useState } from 'react';
import { ShieldCheck, Info } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { computeTrustScoreV2, computeTutorTrustScore } from '../lib/trustScoreV2';

// Re-export the V1 helper so existing imports from this module keep working.
export { computeTutorTrustScore };

const TIER_META = {
  excellent: { color: '#10b981', ar: 'ممتاز',  en: 'Excellent' },
  trusted:   { color: '#0ea5e9', ar: 'موثوق',  en: 'Trusted'   },
  good:      { color: '#f59e0b', ar: 'جيد',    en: 'Good'      },
  limited:   { color: '#ef4444', ar: 'محدود',  en: 'Limited'   },
};

const FACTOR_LABELS = {
  identity:     { ar: 'الهوية والتحقق',         en: 'Identity & Verification' },
  experience:   { ar: 'الخبرة والتعليم',         en: 'Experience & Education' },
  rating:       { ar: 'التقييم وجودته',          en: 'Reviews & Rating Quality' },
  confirmation: { ar: 'معدّل تأكيد الحجوزات',    en: 'Booking Confirmation' },
  completion:   { ar: 'معدّل إكمال الدروس',      en: 'Lesson Completion' },
  recency:      { ar: 'النشاط الأخير',           en: 'Recency / Activity' },
};

/**
 * TrustScoreBadge V2 — circular ring badge with optional hover-tooltip
 * showing the per-factor breakdown.
 *
 *   <TrustScoreBadge tutor={tutor} stats={bookingStats} size="md" showBreakdown />
 *
 * Props:
 *   - tutor:          required tutor profile
 *   - stats:          optional booking aggregation (confirmed/rejected/completed)
 *   - size:           'sm' | 'md' | 'lg'  (default 'sm')
 *   - showBreakdown:  whether to render the hover tooltip (default true on md+lg)
 *   - language:       legacy prop (the badge now reads from useLanguage())
 */
const TrustScoreBadge = ({
  tutor,
  stats,
  size = 'sm',
  showBreakdown,
  language: _legacy,
}) => {
  const { language, localize } = useLanguage();
  const isAr = language === 'ar';
  const [open, setOpen] = useState(false);

  const result = computeTrustScoreV2(tutor, stats);
  const meta = TIER_META[result.tier] || TIER_META.limited;
  const enableBreakdown = showBreakdown ?? (size !== 'sm');

  const dim = size === 'lg' ? 56 : size === 'md' ? 44 : 36;
  const stroke = size === 'lg' ? 5 : 4;
  const r = (dim - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (result.score / 100) * c;

  const tierLabel = localize({ ar: meta.ar, en: meta.en });

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: dim, height: dim }}
      onMouseEnter={() => enableBreakdown && setOpen(true)}
      onMouseLeave={() => enableBreakdown && setOpen(false)}
      onFocus={() => enableBreakdown && setOpen(true)}
      onBlur={() => enableBreakdown && setOpen(false)}
      tabIndex={enableBreakdown ? 0 : -1}
      data-testid={`trust-score-${result.score}`}
      title={`${localize({ ar: 'مؤشر الثقة', en: 'Trust Score' })}: ${result.score}/100 — ${tierLabel}`}
      aria-label={`Trust Score ${result.score}/100`}
    >
      <svg width={dim} height={dim} className="-rotate-90">
        <circle
          cx={dim / 2} cy={dim / 2} r={r}
          stroke="rgba(148,163,184,0.18)" strokeWidth={stroke} fill="none"
        />
        <circle
          cx={dim / 2} cy={dim / 2} r={r}
          stroke={meta.color} strokeWidth={stroke} strokeLinecap="round" fill="none"
          strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 600ms ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span
          className={`font-black tabular-nums ${size === 'lg' ? 'text-base' : size === 'md' ? 'text-sm' : 'text-[11px]'}`}
          style={{ color: meta.color }}
        >
          {result.score}
        </span>
        {size === 'lg' && (
          <span className="text-[8px] tracking-widest uppercase mt-0.5" style={{ color: meta.color }}>
            {tierLabel}
          </span>
        )}
      </div>

      {/* Hover/Focus breakdown tooltip */}
      {enableBreakdown && open && (
        <div
          role="dialog"
          className={`absolute z-50 ${isAr ? 'right-0' : 'left-0'} top-[calc(100%+8px)]
            w-64 rounded-xl glass-pop p-3.5 text-left
            text-slate-700 dark:text-slate-200`}
          data-testid={`trust-score-breakdown-${result.score}`}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-indigo-500/80">
                {localize({ ar: 'مؤشر الثقة', en: 'Trust Score' })}
              </p>
              <p className="text-2xl font-black tabular-nums leading-none mt-0.5" style={{ color: meta.color }}>
                {result.score}
                <span className="text-xs font-bold text-slate-400 ms-1">/ 100</span>
              </p>
              <p className="text-[11px] font-bold uppercase tracking-wider mt-0.5" style={{ color: meta.color }}>
                {tierLabel}
              </p>
            </div>
            <ShieldCheck className="w-5 h-5 mt-1" style={{ color: meta.color }} />
          </div>
          <div className="space-y-1.5">
            {result.breakdown.map((b) => {
              const label = FACTOR_LABELS[b.key];
              const pct = (b.earned / b.max) * 100;
              return (
                <div key={b.key} className="flex items-center gap-2.5 text-[11px]">
                  <span className="flex-1 font-semibold truncate text-slate-600 dark:text-slate-300">
                    {label ? localize(label) : b.label}
                  </span>
                  <span className="relative w-20 h-1.5 rounded-full bg-slate-200/60 dark:bg-slate-700/40 overflow-hidden">
                    <span
                      className="absolute inset-y-0 start-0 rounded-full"
                      style={{ width: `${pct}%`, background: meta.color }}
                    />
                  </span>
                  <span className="tabular-nums font-mono text-slate-500 w-9 text-right">
                    {b.earned}/{b.max}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="mt-3 pt-2 border-t border-slate-200/40 dark:border-white/5 text-[10px] text-slate-500 leading-snug flex gap-1.5">
            <Info className="w-3 h-3 mt-px shrink-0" />
            {localize({
              ar: 'يُحدَّث المؤشر تلقائياً مع كل حجز ومراجعة.',
              en: 'Score updates live with every booking and review.',
            })}
          </p>
        </div>
      )}
    </div>
  );
};

/** Larger pill-style badge for detailed profile views. */
export const TrustScorePill = ({ tutor, stats, language: _legacy }) => {
  const { language, localize } = useLanguage();
  const isAr = language === 'ar';
  const result = computeTrustScoreV2(tutor, stats);
  const palette = {
    excellent: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
    trusted:   'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800',
    good:      'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    limited:   'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${palette[result.tier]}`}
      data-testid={`trust-score-pill-${result.score}`}
    >
      <ShieldCheck className="w-3 h-3" />
      {localize({ ar: 'الثقة', en: 'Trust' })} {result.score}
    </span>
  );
};

export default TrustScoreBadge;
